import { useState, useEffect, ReactNode, useCallback } from 'react';
import FigurService from '../services/figureService';
import { useQueryClient } from '@tanstack/react-query';
import { FavoritesContext } from './FavoritesContextInstance';
import { useMutation } from '@tanstack/react-query';
import { useConfig } from '../config/env';

// Re-export the context so imports in other files still work
export { FavoritesContext } from './FavoritesContextInstance';

// Type for query data structure
type QueryData = {
  pages: Array<{
    data: Array<{
      imageId: number;
      customerFavorite: boolean;
      [key: string]: unknown;
    }>;
    totalCount?: number;
    hasMore?: boolean;
    nextCursor?: string | null;
  }>;
  pageParams?: number[];
};

// Only export the provider component
export const FavoritesProvider = ({ children }: { children: ReactNode }) => {
  const [favorites, setFavorites] = useState<Set<string>>(new Set());
  const queryClient = useQueryClient();

  // Load favorites from backend
  const loadFavorites = useCallback(async () => {
    try {
      const response = await FigurService.getFavoriteFigures(1, 100, 0);
      const favoriteIds = new Set(response.data.map((fig) => fig.imageId.toString()));
      setFavorites(favoriteIds);
      console.log(`Loaded ${favoriteIds.size} favorites from server`);
    } catch (error) {
      console.error('Error loading favorites:', error);
    }
  }, []); // Empty deps - this function doesn't depend on any state

  // Public method to refresh favorites
  const refreshFavorites = useCallback(async () => {
    await loadFavorites();
  }, [loadFavorites]); // This is fine since loadFavorites is stable

  // Load favorites on initial render
  useEffect(() => {
    loadFavorites();
  }, [loadFavorites]); // This is fine since loadFavorites is stable

  // Define query key prefixes centrally
  // const QUERY_KEYS = ['figures', 'figures-grid'] as const;

  const cfg = useConfig();

  const toggleFavoriteMutation = useMutation({
    mutationFn: async ({ id, collectionId, newFavoriteState }: { id: string; collectionId: number; newFavoriteState: boolean }) => {
      const figure = await FigurService.getFigureByIdAndCollection(Number(id), collectionId);
      if (!figure) throw new Error(`Figure ${id} not found`);
      const updatedFigure = { ...figure, customerFavorite: newFavoriteState };
      return await FigurService.saveFigure(updatedFigure, collectionId);
    },
    onMutate: async (vars) => {
      const { id, collectionId, newFavoriteState } = vars;
      console.log(`toggleFavorite onMutate: id=${id}, collectionId=${collectionId}, newState=${newFavoriteState}`);

      const favoritterCollectionId = Number(cfg?.favoritterCollectionId ?? 0);
      const isRemovingFromFavorites = collectionId === favoritterCollectionId && !newFavoriteState;

      // Cancel any outgoing refetches so they don't overwrite our optimistic update
      if (isRemovingFromFavorites) {
        // Cancel queries for both grid and list views (all seriesId variations)
        await queryClient.cancelQueries({ 
          predicate: (query) => {
            const key = query.queryKey;
            return (
              (key[0] === 'figures-grid' && key[1] === favoritterCollectionId) ||
              (key[0] === 'figures' && key[1] === favoritterCollectionId)
            );
          }
        });
      } else {
        await queryClient.cancelQueries({ queryKey: ['figures-grid', 0] });
      }

      // Snapshot the previous values for rollback
      const previousFavoritesGridQuery = queryClient.getQueryData(['figures-grid', favoritterCollectionId]);
      const previousFavoritesListQueries = queryClient.getQueriesData({ 
        predicate: (query) => {
          const key = query.queryKey;
          return key[0] === 'figures' && key[1] === favoritterCollectionId;
        }
      });

      // Optimistically update to the new value
      if (isRemovingFromFavorites) {
        // Update grid view cache
        queryClient.setQueryData<QueryData>(['figures-grid', favoritterCollectionId], (oldData) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            pages: oldData.pages.map((page) => ({
              ...page,
              data: page.data.filter((figure) => figure.imageId !== Number(id)),
              totalCount: Math.max(0, (page.totalCount || 0) - 1),
            })),
          };
        });

        // Update list view caches (all seriesId variations)
        previousFavoritesListQueries.forEach(([queryKey]) => {
          queryClient.setQueryData<QueryData>(queryKey, (oldData) => {
            if (!oldData) return oldData;
            return {
              ...oldData,
              pages: oldData.pages.map((page) => ({
                ...page,
                data: page.data.filter((figure) => figure.imageId !== Number(id)),
                totalCount: Math.max(0, (page.totalCount || 0) - 1),
              })),
            };
          });
        });
      }

      // Snapshot previous state for rollback
      const previousFavorites = new Set(favorites);

      // Optimistically update local favorites for instant UI feedback (e.g., heart icon)
      setFavorites((prev) => {
        const newFavorites = new Set(prev);
        if (newFavoriteState) {
          newFavorites.add(id);
        } else {
          newFavorites.delete(id);
        }
        return newFavorites;
      });

      return { previousFavorites, previousFavoritesGridQuery, previousFavoritesListQueries };
    },
    onError: (err, _vars, context) => {
      // Roll back the optimistic update on failure
      if (context) {
        setFavorites(context.previousFavorites);
        // Roll back the query cache if we have snapshots
        if (context.previousFavoritesGridQuery) {
          queryClient.setQueryData(['figures-grid', Number(cfg?.favoritterCollectionId ?? 0)], context.previousFavoritesGridQuery);
        }
        if (context.previousFavoritesListQueries) {
          context.previousFavoritesListQueries.forEach(([queryKey, oldData]) => {
            if (oldData) {
              queryClient.setQueryData(queryKey, oldData);
            }
          });
        }
      }
      console.error('Error toggling favorite:', err);
      // Invalidate to refetch and get back to a known good server state
      queryClient.invalidateQueries({ queryKey: ['figures-grid'] });
      queryClient.invalidateQueries({ queryKey: ['figures'] });
    },
    onSuccess: () => {
      console.debug('toggleFavorite success: invalidating figures queries');
      // Invalidate queries to ensure all views get the freshest data.
      queryClient.invalidateQueries({ queryKey: ['figures-grid'] });
      queryClient.invalidateQueries({ queryKey: ['figures'] });
    },
  });

  const toggleFavorite = useCallback(
    async (id: string, collectionId: number) => {
      const isCurrentlyFavorite = favorites.has(id);
      const newState = !isCurrentlyFavorite;
      console.debug(`toggleFavorite called: id=${id}, collectionId=${collectionId}, from=${isCurrentlyFavorite} to=${newState}`);
      toggleFavoriteMutation.mutate({ id, collectionId, newFavoriteState: newState });
    },
    [toggleFavoriteMutation, favorites],
  );

  const isFavorite = useCallback(
    (id: string): boolean => {
      return favorites.has(id);
    },
    [favorites],
  );

  return <FavoritesContext.Provider value={{ favorites, toggleFavorite, isFavorite, refreshFavorites }}>{children}</FavoritesContext.Provider>;
};
